import { useCallback } from 'react';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { trackAddToCart } from '@app/infrastructure/gtm/transport/trackAddToCart';
import { GtmItemCategory } from '@app/domain/model/gtm/GtmItemCategory';
import { AddToCartData } from '@app/domain/model/gtm/AddToCartData';
import { ensureString } from '@app/lib/util/ensureString';
import { PremiumType } from '@app/domain/model/eshop/transport/PremiumType';

interface UseTransportGtmTrackingParams {
    selectedCascoPremium: Nullable<TransportPremiumsResponse>;
    selectedMtplPremium: Nullable<TransportPremiumsResponse>;
    selectedRiders: ProductDataRider[];
    insurancePackage: string;
    availableRiders: ProductDataRider[];
}

interface TrackStepCompletionParams {
    currentStepKey: string;
}

export function useTransportGtmTracking({
    selectedCascoPremium,
    selectedMtplPremium,
    selectedRiders,
    insurancePackage,
    availableRiders,
}: UseTransportGtmTrackingParams) {
    const trackStepCompletion = useCallback(
        ({ currentStepKey }: TrackStepCompletionParams) => {
            const isAdditionalRisksVisible = availableRiders.length > 0;
            const isCascoStepVisible =
                insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl;

            let shouldTrack = false;

            if (isAdditionalRisksVisible && currentStepKey === 'step-2-3') {
                shouldTrack = true;
            } else if (!isAdditionalRisksVisible && isCascoStepVisible && currentStepKey === 'step-2-2') {
                shouldTrack = true;
            } else if (!isAdditionalRisksVisible && !isCascoStepVisible && currentStepKey === 'step-2-1') {
                shouldTrack = true;
            }

            if (!shouldTrack) {
                return;
            }

            const items: AddToCartData[] = [];

            if (insurancePackage === PremiumType.Mtpl || insurancePackage === PremiumType.CascoMtpl) {
                if (selectedMtplPremium) {
                    items.push({
                        itemName: selectedMtplPremium.title,
                        itemId: selectedMtplPremium.code,
                        price: selectedMtplPremium.premiumAfterDiscount ?? selectedMtplPremium.premium ?? 0,
                        itemCategory: GtmItemCategory.Mtpl,
                        itemVariant: selectedMtplPremium.riskPackageCode,
                    });
                }
            }

            if (insurancePackage === PremiumType.Casco || insurancePackage === PremiumType.CascoMtpl) {
                if (selectedCascoPremium) {
                    items.push({
                        itemName: selectedCascoPremium.title,
                        itemId: selectedCascoPremium.code,
                        price: selectedCascoPremium.premiumAfterDiscount ?? selectedCascoPremium.premium ?? 0,
                        itemCategory: GtmItemCategory.Casco,
                        itemVariant: selectedCascoPremium.riskPackageCode,
                    });
                }
            }

            selectedRiders.forEach(rider => {
                if (rider.riderGuid) {
                    items.push({
                        itemName: rider.riderName,
                        itemId: rider.riderGuid,
                        price: rider.premiums.premium,
                        itemCategory: GtmItemCategory.Rider,
                        itemVariant: ensureString(rider.typeCode, undefined),
                    });
                }
            });

            trackAddToCart(items);
        },
        [insurancePackage, selectedMtplPremium, selectedCascoPremium, selectedRiders, availableRiders],
    );

    return {
        trackStepCompletion,
    };
}
